import { useState, useMemo } from 'react'
import {
  TrendingUp,
  Users,
  DollarSign,
  Zap,
  AlertCircle,
  CheckCircle,
  BarChart3,
  PieChart,
  LineChart as LineChartIcon,
  Activity,
  Target,
  Shield,
  Brain,
  Calendar,
  MessageSquare,
  Ticket,
  Clock,
  Filter,
} from 'lucide-react'
import { useTicketsStore } from '../store/ticketsStore'
import { useCompanyGrowthStore } from '../store/companyGrowthStore'
import { useMeetingsStore } from '../store/meetingsStore'

export default function ComprehensiveAnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState('overview')
  const [dateRange, setDateRange] = useState('30days')

  // Data from stores
  const tickets = useTicketsStore((state) => state.getAllTickets())
  const ticketStats = useTicketsStore((state) => state.getStatistics())
  const companyData = useCompanyGrowthStore((state) => state.getFullAnalyticsData())
  const growthMetrics = useCompanyGrowthStore((state) => state.getGrowthMetrics())
  const meetings = useMeetingsStore((state) => state.meetings)

  // Calculated metrics
  const metrics = useMemo(() => {
    // Ticket metrics
    const totalTickets = tickets.length
    const resolvedTickets = tickets.filter(t => t.status === 'RESOLVED').length
    const resolutionRate = totalTickets > 0 ? ((resolvedTickets / totalTickets) * 100).toFixed(1) : 0
    const avgResolutionTime = totalTickets > 0 ? Math.round(Math.random() * 24) : 0 // Mock calculation

    // Meeting metrics
    const totalMeetings = meetings.length
    const upcomingMeetings = meetings.filter(m => new Date(m.startTime) > new Date()).length
    const completedMeetings = meetings.filter(m => new Date(m.startTime) < new Date()).length

    // Business metrics
    const revenueGrowth = parseFloat(growthMetrics.revenueGrowth)
    const customerGrowth = parseFloat(growthMetrics.customerGrowth)

    return {
      tickets: {
        total: totalTickets,
        resolved: resolvedTickets,
        open: tickets.filter(t => t.status === 'OPEN').length,
        escalated: tickets.filter(t => t.status === 'ESCALATED').length,
        resolutionRate,
        avgResolutionTime,
      },
      meetings: {
        total: totalMeetings,
        upcoming: upcomingMeetings,
        completed: completedMeetings,
      },
      business: {
        revenue: growthMetrics.arr,
        mrr: growthMetrics.mrr,
        revenueGrowth,
        customerGrowth,
        customers: companyData.metrics.customers.total,
        retention: companyData.metrics.customers.retention,
      },
    }
  }, [tickets, meetings, growthMetrics, companyData])

  // Priority alerts
  const alerts = useMemo(() => {
    const priorityAlerts = []

    if (metrics.tickets.open > 20) {
      priorityAlerts.push({
        severity: 'high',
        message: `${metrics.tickets.open} open tickets. Response time may be impacted.`,
        icon: AlertCircle,
      })
    }

    if (metrics.business.revenueGrowth < 0) {
      priorityAlerts.push({
        severity: 'critical',
        message: `Revenue declining: ${metrics.business.revenueGrowth}% MoM`,
        icon: TrendingDown,
      })
    }

    if (metrics.business.retention < 90) {
      priorityAlerts.push({
        severity: 'high',
        message: `Customer retention: ${metrics.business.retention}%`,
        icon: Users,
      })
    }

    return priorityAlerts
  }, [metrics])

  const StatCard = ({ label, value, icon: Icon, trend, color }) => (
    <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-blue-200 font-medium">{label}</p>
        <Icon className={`text-${color}-500`} size={20} />
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {trend && <p className={`text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'} mt-2`}>
        {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}% vs last period
      </p>}
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <BarChart3 className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Comprehensive Analytics</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Cross-system Business Intelligence Dashboard</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{metrics.business.customers}</div>
                <div className="text-sm text-blue-100">Total Customers</div>
              </div>
            </div>
          </div>
        </div>

        {/* Critical Alerts */}
        {alerts.length > 0 && (
          <div className="space-y-3">
            {alerts.map((alert, idx) => {
              const AlertIcon = alert.icon
              return (
                <div
                  key={idx}
                  className={`rounded-lg p-4 border flex items-start gap-3 ${
                    alert.severity === 'critical'
                      ? 'bg-red-500/10 border-red-500/30'
                      : 'bg-orange-500/10 border-orange-500/30'
                  }`}
                >
                  <AlertIcon
                    size={20}
                    className={alert.severity === 'critical' ? 'text-red-400' : 'text-orange-400'}
                  />
                  <p className={alert.severity === 'critical' ? 'text-red-300' : 'text-orange-300'}>
                    {alert.message}
                  </p>
                </div>
              )
            })}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-blue-500/20 pb-4 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'tickets', label: 'Support', icon: Ticket },
            { id: 'business', label: 'Business', icon: DollarSign },
            { id: 'meetings', label: 'Meetings', icon: Calendar },
            { id: 'security', label: 'Security', icon: Shield },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600/40 text-blue-200 border border-blue-500/50'
                  : 'text-slate-400 hover:text-blue-300'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Support Tickets"
                value={metrics.tickets.total}
                icon={Ticket}
                trend={-15}
                color="blue"
              />
              <StatCard
                label="Resolution Rate"
                value={`${metrics.tickets.resolutionRate}%`}
                icon={CheckCircle}
                trend={8.5}
                color="green"
              />
              <StatCard
                label="Annual Revenue"
                value={`$${(metrics.business.revenue / 1000).toFixed(0)}K`}
                icon={DollarSign}
                trend={metrics.business.revenueGrowth}
                color="green"
              />
              <StatCard
                label="Active Customers"
                value={metrics.business.customers}
                icon={Users}
                trend={metrics.business.customerGrowth}
                color="cyan"
              />
            </div>

            {/* System Health */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Activity size={20} />
                  System Health
                </h3>
                <div className="space-y-3">
                  {[
                    { name: 'Help Desk', status: 'operational', uptime: 99.9 },
                    { name: 'Security Tools', status: 'operational', uptime: 99.8 },
                    { name: 'Business Analytics', status: 'operational', uptime: 100 },
                    { name: 'Customer Portal', status: 'operational', uptime: 99.7 },
                  ].map((system, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{system.name}</p>
                        <p className="text-xs text-green-400">✓ {system.status}</p>
                      </div>
                      <p className="text-lg font-bold text-green-400">{system.uptime}%</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Brain size={20} />
                  AI Insights
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <p className="text-green-300">✓ Customer retention trending positive</p>
                  </div>
                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-blue-300">↑ Resolution rate improving YoY</p>
                  </div>
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-yellow-300">⚠ Average response time: {metrics.tickets.avgResolutionTime}h</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'tickets' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label="Open Tickets" value={metrics.tickets.open} icon={AlertCircle} color="red" />
              <StatCard label="Resolved" value={metrics.tickets.resolved} icon={CheckCircle} color="green" />
              <StatCard label="Escalated" value={metrics.tickets.escalated} icon={Zap} color="orange" />
              <StatCard label="Avg Resolution" value={`${metrics.tickets.avgResolutionTime}h`} icon={Clock} color="blue" />
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Top Support Channels</h3>
              <div className="space-y-3">
                {[
                  { channel: 'Email', tickets: 45, trend: 'up' },
                  { channel: 'Discord', tickets: 28, trend: 'down' },
                  { channel: 'LinkedIn', tickets: 12, trend: 'up' },
                  { channel: 'Phone', tickets: 8, trend: 'stable' },
                ].map((ch, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-white">{ch.channel}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-white">{ch.tickets}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        ch.trend === 'up' ? 'bg-red-500/20 text-red-300' :
                        ch.trend === 'down' ? 'bg-green-500/20 text-green-300' :
                        'bg-blue-500/20 text-blue-300'
                      }`}>
                        {ch.trend === 'up' ? '↑' : ch.trend === 'down' ? '↓' : '→'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Business Tab */}
        {activeTab === 'business' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard
                label="Monthly Revenue"
                value={`$${(metrics.business.mrr / 1000).toFixed(0)}K`}
                icon={DollarSign}
                trend={metrics.business.revenueGrowth}
                color="green"
              />
              <StatCard
                label="Customer Retention"
                value={`${metrics.business.retention}%`}
                icon={Users}
                color="blue"
              />
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Revenue Breakdown</h3>
              <div className="space-y-3">
                {companyData.products.slice(0, 4).map((product, idx) => {
                  const totalRev = companyData.products.reduce((sum, p) => sum + p.revenue, 0)
                  const percent = ((product.revenue / totalRev) * 100).toFixed(0)
                  return (
                    <div key={idx}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-white text-sm">{product.name}</span>
                        <span className="font-bold text-white">${product.revenue.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-slate-700/30 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full"
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Meetings Tab */}
        {activeTab === 'meetings' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label="Total Meetings" value={metrics.meetings.total} icon={Calendar} color="blue" />
              <StatCard label="Upcoming" value={metrics.meetings.upcoming} icon={Zap} color="orange" />
              <StatCard label="Completed" value={metrics.meetings.completed} icon={CheckCircle} color="green" />
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Upcoming Meetings</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {meetings
                  .filter(m => new Date(m.startTime) > new Date())
                  .slice(0, 5)
                  .map((meeting, idx) => (
                    <div key={idx} className="p-3 bg-slate-700/30 rounded-lg">
                      <p className="text-white font-medium">{meeting.title}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(meeting.startTime).toLocaleString()} • {meeting.attendees?.length || 1} attendees
                      </p>
                    </div>
                  ))
                  .slice(0, 5)}
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Shield size={20} />
                Security Status
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-300 font-bold">✓ All Security Tools Operational</p>
                  <p className="text-xs text-green-400 mt-2">EDR, SIEM, Sandbox, Pen Testing all active</p>
                </div>
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-blue-300 font-bold">✓ No Critical Vulnerabilities</p>
                  <p className="text-xs text-blue-400 mt-2">Last scan: 2 hours ago</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
